import { Modal } from '@design-system/components/modal/modal';
import { sprinkles } from '@design-system/sprinkles.css';
import { Button } from '@design-system/components/button/button';
import { clsx } from 'clsx';
import _ from 'lodash';
import { Library } from './library';
import { Typography } from '@design-system/components/typography/typography';
import { Tab, Tabs } from '@mui/material';
import {
  AddFromLibaryVM,
  useAddFromLibraryVM,
} from '../../view-models/addFromLibrary.viewModel';
import { Folder, Video } from '../../models/videos.domain';
import { ApolloError } from '@apollo/client';
import { ContentFilters } from '@client/new/common/library/helpers';
import { SingleUpload } from './SingleUpload';
import { VideoById } from '@client/new/common/videos/useUpload';
import { IconButton } from '@design-system/components/button/iconButton';
import {
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus,
} from '@fortawesome/pro-regular-svg-icons';

type AddTemplatesFromLibraryModalProps = {
  isOpen: boolean;
  close: () => void;
  addSelection: (ids: string[]) => void;
  influencerId: number;
  library: {
    videos: Array<Video>;
    folders: Array<Folder>;
    error: ApolloError | undefined;
    loading: boolean;
    refreshLibrary: () => void;
  };
  loading: boolean;
  type: ContentFilters | null;
  addVideo: (value: VideoById) => void;
  action: 'add' | 'update';
};

export const AddTemplatesFromLibraryModal = (
  p: AddTemplatesFromLibraryModalProps,
) => (
  <Modal
    isOpen={p.isOpen}
    onClose={p.close}
    title="Add from library"
    disableBackdropClick={true}
    footerClassName="p-0"
    modalClassName="w-[742px]"
    modalContentClassName="overflow-hidden"
  >
    <Content {...p} />
  </Modal>
);

const Content = (p: AddTemplatesFromLibraryModalProps) => {
  const model = useAddFromLibraryVM({
    influencerId: p.influencerId,
    library: p.library,
    type: p.type,
    action: p.action,
    addVideo: (value) => p.addVideo(value),
    addVideoFromSelection: (ids) => p.addSelection(ids),
  });

  return (
    <div
      className={sprinkles({
        display: 'flex',
        flexDirection: 'column',
        gap: 'xl',
      })}
    >
      <div className="flex justify-between">
        <Tabs
          value={model.activeTab}
          onChange={(_, t) => model.dispatch('selectTab', t)}
        >
          <Tab value="Library" label={<Typography>Library</Typography>} />
          <Tab value="Upload" label={<Typography>Upload</Typography>} />
        </Tabs>
        <IconButton
          variant="text"
          color="neutral"
          icon={
            model.smallerView ? faMagnifyingGlassPlus : faMagnifyingGlassMinus
          }
          onClick={() => model.dispatch('toggleViewSize')}
        />
      </div>
      <TabContent {...model} loading={p.loading} videoType={p.type} />
      <div
        className={clsx(
          'flex justify-between items-center px-6 py-4 border-t',
          sprinkles({ borderColor: 'N-40' }),
        )}
      >
        {model.footerButtons.map((b) => (
          <Button key={b.label} {...b.props}>
            {b.label}
          </Button>
        ))}
      </div>
    </div>
  );
};

const TabContent = (p: AddFromLibaryVM & { loading: boolean }) => {
  switch (p.activeTab) {
    case 'Library':
      return (
        <Library
          {...p.library}
          loading={p.loading}
          videoType={p.videoType}
          smallerView={p.smallerView}
        />
      );
    case 'Upload':
      return <SingleUpload {...p} />;
  }
};
